import React from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  X as XIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { operationalBrandColors } from '../../../design-system/tokens';

const getArticlePublicUrl = (article) => {
  if (!article?._id || typeof window === 'undefined') return '';
  return `${window.location.origin}/article/${article._id}`;
};

const getArticleOriginalUrl = (article) => (
  article?.siteUrl ||
  article?.url ||
  article?.videoUrl ||
  ''
);

const openExternalUrl = (url) => {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const ArticleShareMenu = ({
  anchorEl,
  article,
  originalUrl,
  publicUrl,
  title,
  description,
  onClose,
  onCreateNewsletter,
  onNotify,
  showEmail,
  showNewsletter,
  showPublicLinkActions
}) => {
  const open = Boolean(anchorEl) && Boolean(article?._id || originalUrl || publicUrl);

  if (!article && !originalUrl && !publicUrl) {
    return null;
  }

  const resolvedOriginalUrl = originalUrl || getArticleOriginalUrl(article);
  const resolvedPublicUrl = publicUrl || getArticlePublicUrl(article);
  const primaryShareUrl = resolvedOriginalUrl || resolvedPublicUrl;
  const shareTitle = title || article?.title || 'Artigo';
  const shareDescription = description || article?.description || article?.title || '';
  const primaryLabel = resolvedOriginalUrl ? 'original' : 'público';
  const hasDistinctPublicUrl = Boolean(
    resolvedPublicUrl &&
    resolvedOriginalUrl &&
    resolvedPublicUrl !== resolvedOriginalUrl
  );

  const closeAfter = (action) => {
    action?.();
    onClose();
  };

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: <WhatsAppIcon sx={{ color: operationalBrandColors.whatsapp }} />,
      onClick: () => closeAfter(() => openExternalUrl(`https://wa.me/?text=${encodeURIComponent(`${shareTitle}\n\n${primaryShareUrl}`)}`))
    },
    {
      name: 'Telegram',
      icon: <TelegramIcon sx={{ color: operationalBrandColors.telegram }} />,
      onClick: () => closeAfter(() => openExternalUrl(`https://t.me/share/url?url=${encodeURIComponent(primaryShareUrl)}&text=${encodeURIComponent(shareTitle)}`))
    },
    {
      name: 'Twitter/X',
      icon: <XIcon sx={{ color: operationalBrandColors.twitter }} />,
      onClick: () => closeAfter(() => openExternalUrl(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(primaryShareUrl)}`))
    },
    {
      name: 'Facebook',
      icon: <FacebookIcon sx={{ color: operationalBrandColors.facebook }} />,
      onClick: () => closeAfter(() => openExternalUrl(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(primaryShareUrl)}`))
    },
    {
      name: 'LinkedIn',
      icon: <LinkedInIcon sx={{ color: operationalBrandColors.linkedin }} />,
      onClick: () => closeAfter(() => openExternalUrl(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(primaryShareUrl)}`))
    }
  ];

  if (showEmail) {
    shareOptions.push({
      name: 'Email',
      icon: <EmailIcon sx={{ color: operationalBrandColors.email }} />,
      onClick: () => closeAfter(() => {
        window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareDescription}\n\n${primaryShareUrl}`)}`;
      })
    });
  }

  if (showNewsletter) {
    shareOptions.push({
      name: 'Criar Newsletter',
      icon: <EmailIcon sx={{ color: operationalBrandColors.email }} />,
      onClick: () => closeAfter(() => onCreateNewsletter?.(article))
    });
  }

  const copyLink = async (url, label) => {
    try {
      await navigator.clipboard.writeText(url);
      onNotify?.(`${label} copiado para a área de transferência!`);
    } catch (error) {
      onNotify?.('Erro ao copiar link');
    }
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          minWidth: 240,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          bgcolor: 'background.paper',
          backgroundImage: 'none'
        }
      }}
    >
      <Typography variant="subtitle2" sx={{ px: 2, py: 1.5, fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
        Compartilhar link {primaryLabel}
      </Typography>
      <Divider />
      {shareOptions.map((option) => (
        <MenuItem key={option.name} onClick={option.onClick} sx={{ py: 1.5 }}>
          <ListItemIcon>
            {option.icon}
          </ListItemIcon>
          <ListItemText primary={option.name} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
        </MenuItem>
      ))}
      {primaryShareUrl && (
        <MenuItem onClick={() => copyLink(primaryShareUrl, resolvedOriginalUrl ? 'Link original' : 'Link público')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <CopyIcon color="action" />
          </ListItemIcon>
          <ListItemText primary={resolvedOriginalUrl ? 'Copiar link original' : 'Copiar link público'} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
        </MenuItem>
      )}

      {showPublicLinkActions && hasDistinctPublicUrl && (
        <>
          <Divider />
          <Typography variant="subtitle2" sx={{ px: 2, py: 1.5, fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
            Link Mensure
          </Typography>
          <MenuItem onClick={() => copyLink(resolvedPublicUrl, 'Link público')} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <CopyIcon color="action" />
            </ListItemIcon>
            <ListItemText primary="Copiar link público" primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
          </MenuItem>
          <MenuItem onClick={() => closeAfter(() => openExternalUrl(resolvedPublicUrl))} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <OpenInNewIcon color="action" />
            </ListItemIcon>
            <ListItemText primary="Abrir versão pública" primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
          </MenuItem>
        </>
      )}
    </Menu>
  );
};

ArticleShareMenu.propTypes = {
  anchorEl: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func
  ]),
  article: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    siteUrl: PropTypes.string,
    url: PropTypes.string,
    videoUrl: PropTypes.string
  }),
  originalUrl: PropTypes.string,
  publicUrl: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCreateNewsletter: PropTypes.func,
  onNotify: PropTypes.func,
  showEmail: PropTypes.bool,
  showNewsletter: PropTypes.bool,
  showPublicLinkActions: PropTypes.bool
};

ArticleShareMenu.defaultProps = {
  anchorEl: null,
  article: null,
  originalUrl: '',
  publicUrl: '',
  title: '',
  description: '',
  onCreateNewsletter: null,
  onNotify: null,
  showEmail: true,
  showNewsletter: false,
  showPublicLinkActions: true
};

export default ArticleShareMenu;
